import { createDateWithTimeZoneOffset } from '@dispatch/shared';
import {
  useGetQualifiedResources,
  useGetTask,
  usePermissions,
  useRescheduleTask,
  useTabHandler,
  useSetTaskStatus,
  useSetTaskLockStatus,
  useGetTaskEvents,
  useGetResourcesPaginated,
  useCurrentUserResource,
  useGetGeoStatus,
  useGetUserByExternalId,
  useGetRole,
  useForceReassign,
  useCompany,
  useGetCompanyAttributeOptions,
  useGetResource,
  useGetTaskAssignments,
  useSetTaskUnassigned,
  useGetQualifiedResourcesPost,
} from '@dispatch/shared/hooks';
import {
  QualifiedResourcesFNOLFilters,
  ReassignFormBody,
  TaskAssignmentOrEvent,
  TaskRescheduleRequest,
} from '@dispatch/shared/types';
import { TaskDetails } from '@dispatch/web/features/tasks';
import {
  useNotification,
  useNotificationMutationFactory,
  useResourceDataGrid,
} from '@dispatch/web/ui';
import { useParams } from 'react-router-dom';
import React, { useCallback, useState } from 'react';
import { useAccessToken, useCurrentUser } from '@dispatch/web/auth';
import { useQueryClient } from 'react-query';
import { useValidateTaskErrors } from '../utils/ErrorHandlers';
import { addMinutes } from 'date-fns';
import { isUndefined } from 'lodash';

interface TaskDetailsPageProps {
  navigateToOnClose?: string;
}

export const TaskDetailsPage = ({
  navigateToOnClose,
}: TaskDetailsPageProps = {}) => {
  const { company, perils, rescheduleReasons, featureFlags } = useCompany();
  const { isAdjuster } = useCurrentUserResource();
  const { permissions } = usePermissions();
  const queryClient = useQueryClient();
  const { userId } = useCurrentUser();
  const params = useParams();
  const { notifySuccess, notifyError } = useNotification();
  const { handleError } = useValidateTaskErrors(notifyError);
  const taskId: string = params['taskId'] || '';
  const resourceMeta = useResourceDataGrid();
  const [disableButtons, setDisableButtons] = useState<boolean>();
  const { isAccessTokenSet } = useAccessToken();

  const user = useGetUserByExternalId(userId || '', isAccessTokenSet);
  // Opts into wasManuallyUnassigned for the manual-unassignment designator.
  const { task, taskStatus } = useGetTask(taskId, user.data?.resourceId, [
    'manualUnassignment',
  ]);
  const { taskAssignments } = useGetTaskAssignments(taskId);

  const role = useGetRole(user?.data?.roleId || '');

  const { companyAttributes } = useGetCompanyAttributeOptions(
    company?.attributeDefinition || []
  );

  const { data: assignedResource } = useGetResource({
    id: task?.$msg?.assignedResource?.id || '',
    company,
    enabled: !!task?.$msg?.assignedResource?.id,
  });

  const attributesToRender = React.useMemo(() => {
    try {
      if (isUndefined(task?.$msg?.attributes)) return [];
      if (!Array.isArray(task?.$msg?.attributes)) return [];
      if (!companyAttributes || !Array.isArray(companyAttributes)) return [];

      // Filter out undefined/null definitions
      const definitions =
        companyAttributes?.map((c) => c?.definition).filter((d) => d != null) ||
        [];

      if (definitions.length === 0) return [];

      const attributeDefinitionValues = definitions
        .flatMap((d) => {
          const values = d?.attributeDefinitionValues;
          // ensure attributeDefinitionValues is an array
          return Array.isArray(values) ? values : [];
        })
        .filter((adv) => adv != null);

      const result = task?.$msg?.attributes
        ?.filter((a) => a != null && a?.id)
        ?.map((a) => {
          try {
            return {
              ...a,
              definition: attributeDefinitionValues?.find(
                (d) => d?.integrationGUID === a?.id
              ),
              // find the parent definition for the attribute to allow more context when displaying the attribute
              definitionParent: definitions?.find((def) => {
                const defValues = def?.attributeDefinitionValues;
                return (
                  Array.isArray(defValues) &&
                  defValues?.some((adv) => adv?.integrationGUID === a?.id)
                );
              }),
            };
          } catch (innerError) {
            // eslint-disable-next-line no-console
            console.error('Error processing individual attribute:', {
              attribute: a,
              error: innerError,
              errorMessage:
                innerError instanceof Error
                  ? innerError.message
                  : 'Unknown error',
            });
            return {
              ...a,
              definition: undefined,
              definitionParent: undefined,
            };
          }
        });

      // Ensure we always return an array
      return Array.isArray(result) ? result : [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in attributesToRender:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined,
        taskAttributes: task?.$msg?.attributes,
        companyAttributesLength: Array.isArray(companyAttributes)
          ? companyAttributes.length
          : 'not an array',
        taskId: task?.id,
        hasTask: !!task,
        hasTaskMsg: !!task?.$msg,
      });

      return [];
    }
  }, [task, companyAttributes]);

  const { status: geoStatus } = useGetGeoStatus(
    task?.$geoTracking?.events || []
  );

  const resources = useGetResourcesPaginated({
    filters: { ...resourceMeta?.resourcesFilters, isActive: true },
    enabled: !resourceMeta?.showOnlyQualified,
  });

  const qualifiedResourcesNonEmail = useGetQualifiedResources(
    task?.id,
    resourceMeta?.showOnlyQualified && !featureFlags.qualifiedResourcesFnol
  );

  const resourcesPostFilters = React.useMemo(
    () =>
      !isUndefined(task)
        ? ({
            taskId: task?.id,
            methodOfInspection:
              task?.$msg.assignmentHandling.methodOfInspection,
            severity: task?.$msg.assignmentHandling.severity,
            peril: task?.$msg.claim.peril,
            startTime: task?.$msg.assignmentHandling.appointment,
            endTime:
              task?.$msg.assignmentHandling.appointment &&
              task?.$msg.assignmentHandling.durationInMinutes
                ? addMinutes(
                    new Date(task.$msg.assignmentHandling.appointment),
                    task.$msg.assignmentHandling.durationInMinutes
                  ).toISOString()
                : undefined,
            durationInMinutes: task?.$msg.assignmentHandling.durationInMinutes,
            assignmentCategory:
              task?.$msg.assignmentHandling.assignmentCategory,
            requiredAttributes: task?.requiredAttributes,
          } as QualifiedResourcesFNOLFilters)
        : undefined,
    [task]
  );

  const qualifiedResourcesFnol = useGetQualifiedResourcesPost(
    resourcesPostFilters,
    featureFlags.qualifiedResourcesFnol &&
      resourceMeta?.showOnlyQualified &&
      !isUndefined(resourcesPostFilters)
  );

  const qualifiedResourcesNewData = React.useMemo(() => {
    const filtered = qualifiedResourcesFnol.data
      ?.filter((resource) => resource != null)
      .map((resource) => {
        return {
          ...resource,
          integrationGUID: resource?.id,
        };
      });

    const status =
      qualifiedResourcesFnol.isFetching === true
        ? 'loading'
        : qualifiedResourcesFnol.status;

    return { data: filtered, status };
  }, [
    qualifiedResourcesFnol.data,
    qualifiedResourcesFnol.status,
    qualifiedResourcesFnol.isFetching,
  ]);

  const qualifiedResources = React.useMemo(() => {
    if (featureFlags.qualifiedResourcesFnol) {
      return qualifiedResourcesNewData;
    }
    const status =
      qualifiedResourcesNonEmail.isFetching === true
        ? 'loading'
        : qualifiedResourcesNonEmail.status;
    return {
      data: qualifiedResourcesNonEmail?.data?.map((resource) => ({
        id: resource?.Id,
        resourceName: resource.ResourceName,
      })),
      status,
    };
  }, [
    featureFlags.qualifiedResourcesFnol,
    qualifiedResourcesNewData,
    qualifiedResourcesNonEmail.data,
    qualifiedResourcesNonEmail.status,
    qualifiedResourcesNonEmail.isFetching,
  ]);

  const { taskEvents, taskEventsStatus } = useGetTaskEvents(taskId);

  const latestNaqEvent = React.useMemo(() => {
    return taskEvents
      ?.filter((event) => event.eventCode === 'assignment-sent-to-naq')
      .sort((x, y) => {
        return (
          new Date(x.eventDateTime).getMilliseconds() -
          new Date(y.eventDateTime).getMilliseconds()
        );
      })[0];
  }, [taskEvents]);

  const taskAssignmentAndEventsMerged = React.useMemo<TaskAssignmentOrEvent[]>(
    () => [...(taskAssignments || []), ...(taskEvents || [])],
    [taskAssignments, taskEvents]
  );

  const onRescheduleError = (errorMessage: {
    status: number;
    message: string;
  }) => {
    handleError(errorMessage);
  };

  const notifyStartTaskSuccess = () => {
    notifySuccess('Success', 'Task has been successfully started');
  };

  const notifyStartTaskFailed = () => {
    notifyError('Error', 'Task was not started');
  };

  const notifyCompleteTaskSuccess = () => {
    notifySuccess('Success', 'Task has been successfully completed');
  };

  const notifyCompleteTaskFailed = () => {
    notifyError('Error', 'Task was not completed');
  };

  const onSettled = () => {
    setDisableButtons(false);
  };

  const onForceReassign = (response: {
    success: boolean;
    message?: string;
  }) => {
    if (response?.success) {
      notifySuccess(
        'Task Reassigned Successfully',
        'Task has been successfully reassigned'
      );
      queryClient.invalidateQueries(['resources']);
      queryClient.invalidateQueries(['schedule-data']);
      queryClient.invalidateQueries(['summaries']);
      queryClient.invalidateQueries(['task']);
      queryClient.invalidateQueries(['resource-tasks-assigned']);
      queryClient.invalidateQueries(['resource-tasks-unassigned']);
    } else {
      notifyError('Error', response?.message || 'Tasks were not reassigned');
    }
  };

  const notifications = useNotificationMutationFactory('Task', 'update');
  const rescheduleMutation = useRescheduleTask(() => {
    notifications.notifySuccess();
    refresh();
  }, onRescheduleError);
  const { mutate: reassignTask } = useForceReassign(onForceReassign);
  const { currentTab, setTab } = useTabHandler();
  const startTaskMutation = useSetTaskStatus(
    notifyStartTaskSuccess,
    notifyStartTaskFailed,
    onSettled
  );
  const completeTaskMutation = useSetTaskStatus(
    notifyCompleteTaskSuccess,
    notifyCompleteTaskFailed,
    onSettled
  );

  const handleSave = (form: ReassignFormBody) => {
    const request: TaskRescheduleRequest = {
      resourceId: form.resourceId,
      reasonCode: form.reasonCode,
      appointmentStart: createDateWithTimeZoneOffset(
        form?.assignmentHandling?.appointmentStart,
        form?.claim?.timeZone
      ).toJSON(),
      appointmentEnd: createDateWithTimeZoneOffset(
        form?.assignmentHandling?.appointmentEnd,
        form?.claim?.timeZone
      ).toJSON(),
      isPrecise: false,
    };

    rescheduleMutation.mutate({
      taskId: task?.id || '',
      request,
    });
  };

  const handleForceReassign = (resourceId: string, reasonCode: string) => {
    reassignTask({
      taskIds: [task?.id || ''],
      resourceId: resourceId,
      reasonCode: reasonCode,
    });
  };

  const statuses = () => {
    if (taskStatus === 'loading') {
      return 'loading';
    }

    return 'success';
  };

  const getTaskEventsStatus = () => {
    if (taskEventsStatus === 'loading') {
      return 'loading';
    }

    return 'success';
  };

  const notifyCancelTaskSuccess = () => {
    notifySuccess('', 'Task has been successfully cancelled');
  };

  const notifyCancelTaskFailed = () => {
    notifyError('Error', 'Task was not cancelled');
  };

  const setCancelStatusMutation = useSetTaskStatus(
    notifyCancelTaskSuccess,
    notifyCancelTaskFailed
  );
  const handleCancelTask = useCallback(
    (value) => {
      setCancelStatusMutation.mutate({
        taskId: task?.id || '',
        status: 'cancelled',
        request: { reason: value },
      });
    },
    [setCancelStatusMutation, task?.id]
  );

  const {
    setLockedStatusMutation,
    setLockStatusInCache,
    invalidateLockStatus,
  } = useSetTaskLockStatus();

  const handleLockTask = async () => {
    const lockedAt = task?.$msg.lockedAt;
    const statusLabel = lockedAt ? 'unlocked' : 'locked';
    try {
      setLockStatusInCache(task?.id);
      await setLockedStatusMutation.mutateAsync({
        taskId: task?.id || '',
        status: statusLabel,
      });
      notifySuccess('', `Task has been successfully ${statusLabel}`);
      invalidateLockStatus(task?.id);
    } catch {
      notifyError('Error', `Task was not ${statusLabel}`);
      invalidateLockStatus(task?.id);
    }
  };

  const handleStartTask = useCallback(() => {
    setDisableButtons(true);

    startTaskMutation.mutate({
      taskId: task?.id || '',
      status: 'pending',
    });
  }, [startTaskMutation, task?.id]);

  const handleCompleteTask = useCallback(() => {
    setDisableButtons(true);
    completeTaskMutation.mutate({
      taskId: task?.id || '',
      status: 'completed',
    });
  }, [completeTaskMutation, task?.id]);

  const resourceResolver = React.useMemo(() => {
    if (resourceMeta?.showOnlyQualified) {
      // this needs to be casted to this type until the api has a more consistent structure.
      // mui data grid isnt allowing multiple rowId mappings, so this is a fix for that.
      return qualifiedResources;
    } else {
      return resources;
    }
  }, [resourceMeta?.showOnlyQualified, resources, qualifiedResources]);

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries(['resources']);
  }, [queryClient]);

  const successUnassigned = () => {
    notifySuccess('Task Unassigned', 'Task has been successfully unassigned');
  };

  const errorUnassigned = (errorMessage: string) => {
    notifyError(errorMessage || 'Task was not unassigned', '');
  };

  const { mutate: setStatusUnassigned } = useSetTaskUnassigned(
    successUnassigned,
    errorUnassigned
  );

  const handleUnassignTask = useCallback(() => {
    setDisableButtons(true);
    setStatusUnassigned({ taskId: task?.id || '' });
  }, [setStatusUnassigned, task?.id]);

  return (
    <TaskDetails
      data={task}
      company={company}
      showOnlyQualified={{
        value: resourceMeta?.showOnlyQualified,
        setShowOnlyQualified: resourceMeta?.setShowOnlyQualified,
      }}
      resources={resourceResolver || []}
      onSave={handleSave}
      onForceReassign={handleForceReassign}
      onCancelTask={handleCancelTask}
      onLockTask={handleLockTask}
      onStartTask={handleStartTask}
      onCompleteTask={handleCompleteTask}
      status={statuses()}
      permissions={permissions}
      currentTab={currentTab}
      setTab={setTab}
      perils={perils}
      taskEvents={taskAssignmentAndEventsMerged}
      getTaskEventsStatus={getTaskEventsStatus()}
      rescheduleReasons={rescheduleReasons}
      notifyError={notifyError}
      resourceMeta={resourceMeta}
      disableButtons={disableButtons}
      isAdjuster={isAdjuster}
      geoStatus={geoStatus}
      cancelStatus={setCancelStatusMutation.status}
      tabLabels={
        task?.$msg?.claim?.supplementalData &&
        task.$msg.claim.supplementalData.length > 0
          ? ['GENERAL', 'LOCATIONS', 'NOTES', 'TIMELINE', 'SUPPLEMENTAL DATA']
          : undefined
      }
      isInternalAdmin={role?.data?.data?.roleType === 'internal-admin'}
      attributes={attributesToRender}
      assignedResource={assignedResource}
      latestNaqEvent={latestNaqEvent}
      handleUnassignTask={handleUnassignTask}
      navigateToOnClose={navigateToOnClose}
    />
  );
};
